import { z } from 'zod';
import { pagedSchema } from '@/shared/reference/wire';
export const creditProductOptionSchema = z.looseObject({ id: z.string(), name: z.string() });
export const creditProductOptionPageSchema = pagedSchema(creditProductOptionSchema);
export type CreditProductOption = z.infer<typeof creditProductOptionSchema>;

export const creditProductListItemSchema = creditProductOptionSchema.extend({
  tenantDisplay: z.string().nullish(),
  categoryName: z.string().nullish(),
  instrumentType: z.string().nullish(),
});
export const creditProductListPageSchema = pagedSchema(creditProductListItemSchema);
export type CreditProductListItem = z.infer<typeof creditProductListItemSchema>;

const text = z.string().nullish();
const num = z.number().nullish();
const bool = z.boolean().nullish();
const numOrText = z.union([z.number(), z.string()]).nullish();

/** Linha da lista da tela (`CreditProductList.tsx:34-172`): além da porta F1a, as colunas exibidas. */
export const creditProductRowSchema = creditProductListItemSchema.extend({
  amortizationType: text,
  amortizationTypeDisplay: text,
  termStatus: text,
  termStatusDisplay: text,
  createdAt: text,
});
export type CreditProductRow = z.infer<typeof creditProductRowSchema>;
export const creditProductRowPageSchema = pagedSchema(creditProductRowSchema);

/** `IUploadReadModel` do legado (`utils/interface`). */
export const uploadSchema = z.looseObject({
  id: text,
  fileName: text,
  displayName: text,
  fileType: text,
  tempGetUrl: text,
  tempPutUrl: text,
});
export type UploadDoc = z.infer<typeof uploadSchema>;

export const relatedPersonSchema = z.looseObject({
  id: text,
  productId: text,
  personId: z.string(),
  personIdDisplay: text.meta({ pii: 'name' }),
  discriminator: text,
  personDiscriminator: text,
  typeOfRelationship: z.string(),
  typeOfRelationshipDisplay: text,
  relatedToId: text,
  relatedToIdDisplay: text.meta({ pii: 'name' }),
  signatureType: text,
  signatureTypeDisplay: text,
  signatureValidation: text,
  signatureValidationDisplay: text,
});
export type RelatedPerson = z.infer<typeof relatedPersonSchema>;

export const requiredDocSchema = z.looseObject({
  id: text,
  productId: text,
  personType: z.string(),
  fileType: z.string(),
  group: z.union([z.string(), z.number()]),
  typeOfRelationship: text,
});
export type RequiredDoc = z.infer<typeof requiredDocSchema>;

/** `ICommissionRangeDatailsCreateModel` (`types/gererics.ts:89`) + campos do formulário. */
export const commissionDetailSchema = z.looseObject({
  name: text,
  amount: num.meta({ unit: 'cents-or-fraction' }),
  type: text,
  baseValue: text,
  chargeCommissionFrom: text,
  isCustomerRegistrationFee: bool,
  financeCost: bool,
});
export type CommissionDetail = z.infer<typeof commissionDetailSchema>;

export const commissionRangeSchema = z.looseObject({
  id: text,
  minimumPrincipalAmount: num.meta({ unit: 'cents' }),
  maximumPrincipalAmount: num.meta({ unit: 'cents' }),
  details: z.array(commissionDetailSchema).nullish(),
});
export type CommissionRange = z.infer<typeof commissionRangeSchema>;

/** `ISignatureDocumentTemplateModel` (`types/gererics.ts:44-55`). */
export const signatureTemplateSchema = z.looseObject({
  id: text,
  uploadId: text,
  creditProductId: text,
  name: z.string(),
  templateType: numOrText,
  templateTypeDisplay: text,
  isDeleted: bool,
  createdAt: text,
  upload: z
    .looseObject({
      id: text,
      displayName: text,
      fileName: text,
      fileType: text,
      tempGetUrl: text,
    })
    .nullish(),
  _clientId: z.string().optional(),
});
export type SignatureTemplate = z.infer<typeof signatureTemplateSchema>;

export const employmentStatusSchema = z.looseObject({ id: text, code: text, name: text });
export type EmploymentStatus = z.infer<typeof employmentStatusSchema>;

export const governmentDepartmentSchema = z.looseObject({
  id: z.string(),
  name: text,
  internalId: text,
  employmentStatus: z.array(employmentStatusSchema).nullish(),
});
export type GovernmentDepartment = z.infer<typeof governmentDepartmentSchema>;
export const governmentDepartmentPageSchema = pagedSchema(governmentDepartmentSchema);

/** `ICreditProductRulesReadModel` (`types/productRulesTypes.ts:34`). */
export const productRulesSchema = z.looseObject({
  id: z.string(),
  name: z.string(),
  rulesType: numOrText,
  description: text,
  rules: z.array(z.looseObject({})).nullish(),
});
export type ProductRules = z.infer<typeof productRulesSchema>;
export const productRulesPageSchema = pagedSchema(productRulesSchema);

export const ruleFieldSchema = z.looseObject({ id: z.string(), fieldDisplay: text });
export const ruleFieldListSchema = z.array(ruleFieldSchema);

export const modalitySchema = z.looseObject({
  id: z.string(),
  code: numOrText,
  description: text,
});
export type Modality = z.infer<typeof modalitySchema>;
export const modalityPageSchema = pagedSchema(modalitySchema);

/** `AvailableWalletsResponse` (`creditProduct.ts:62-68`). */
export const availableWalletsSchema = z.looseObject({
  restricted: z.boolean(),
  required: bool,
  suggestedDefault: num,
  suggestedDefaultName: text,
  wallets: z.array(z.looseObject({ code: z.number(), name: text })),
});
export type AvailableWallets = z.infer<typeof availableWalletsSchema>;

const ownerSchema = z.looseObject({
  userId: text,
  userIdDisplay: text.meta({ pii: 'name' }),
  tenantName: text,
  tenant: text,
  groupId: text,
  groupDisplay: text,
});

/** `CreditProductReadModel` (`types/creditProductReadModel.ts:52-179`); tudo opcional, o GET manda o resto. */
export const creditProductSchema = z.looseObject({
  id: z.string(),
  name: z.string(),
  tenant: text,
  tenantDisplay: text,
  ownerUser: ownerSchema.nullish(),
  ownerGroup: ownerSchema.nullish(),
  createdAt: text,
  createdBy: ownerSchema.nullish(),
  updatedAt: text,
  updatedBy: ownerSchema.nullish(),
  instrumentType: text,
  categoryName: text,
  code: num,
  amortizationType: text,
  amortizationTypeDisplay: text,
  viewType: text,
  termStatus: text,
  termStatusDisplay: text,
  templateDoc: uploadSchema.nullish(),
  warrantyRegistrationOfficeTemplateDoc: uploadSchema.nullish(),
  finishedGenerationTimelineTemplateDoc: uploadSchema.nullish(),
  insuranceCancellationTemplateDoc: uploadSchema.nullish(),
  blackEndorsementTemplateDoc: uploadSchema.nullish(),
  signaturePortalProvider: numOrText,
  signatureType: numOrText,
  useSignaturePortal: bool,
  walletCode: numOrText,
  billingSettingId: text,
  communicationSettingId: text,
  payrollAgreementId: text,
  payrollAgreement: z.looseObject({ id: text, name: text }).nullish(),
  relatedPerson: z.array(relatedPersonSchema).nullish(),
  requiredDoc: z.array(requiredDocSchema).nullish(),
  commissionRange: z.array(commissionRangeSchema).nullish(),
  signatureDocumentTemplates: z.array(signatureTemplateSchema).nullish(),
  governmentDepartments: z.array(governmentDepartmentSchema).nullish(),
  creditProductCreditRules: productRulesSchema.nullish(),
  creditProductComplianceRules: productRulesSchema.nullish(),
  creditProductDynamicRules: productRulesSchema.nullish(),
  creditProductCreditRulesId: text,
  creditProductComplianceRulesId: text,
  creditProductDynamicRulesId: text,
});
export type CreditProduct = z.infer<typeof creditProductSchema>;

/** Criação devolve o id (texto); edição com `returnValue=Full`, o produto inteiro. */
export const savedSchema = z.union([z.string(), creditProductSchema]);
export type SavedCreditProduct = z.infer<typeof savedSchema>;

export type AssignBody = {
  userId?: string | undefined;
  groupName?: string | undefined;
  tenant?: string | undefined;
};
export type ShareBody = { permissionLevelType: string; principalId: string; principalType: string };

/** Corpo do `POST /SignatureDocumentTemplate/{id}` (`services/creditProductTemplate/creditProductTemplate.ts:11-22`). */
export interface TemplateBody {
  name: string;
  templateType: string;
  isDeleted: boolean;
  upload: {
    ownerUser: { userId: string; tenantName: string };
    ownerGroup: { groupId: string; tenantName: string };
    fileType: string;
    fileName: string;
    displayName: string;
  };
}
